import { CommonModule, DecimalPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, OnInit, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';

// ── Types ──────────────────────────────────────────────────────────────────

export interface NavItem {
id: string;
label: string;
icon: string;
hasArrow?: boolean;
}

export interface LayerItem {
id: string;
label: string;
visible: boolean;
}

export interface ProjectSummary {
highway: string;
package: string;
totalLength: string;
startChainage: string;
endChainage: string;
state: string;
contractor: string;
budget: string;
consultants: string;
}

export interface ProgressItem {
label: string;
pct: number;
color: string;
}

export interface Contractor {
name: string;
initial: string;
color: string;
}

export interface Alert {
message: string;
severity: 'warning' | 'danger' | 'info';
}

export interface ChainageLabel {
label: string;
top: number;
left: number;
}
@Component({
  selector: 'app-project-planning-dashboard',
  standalone: true,
  imports: [CommonModule, FormsModule, DecimalPipe],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './project-planning-dashboard.component.html',
  styleUrl: './project-planning-dashboard.component.css'
})
export class ProjectPlanningDashboardComponent implements OnInit {
  
    // ── Nav ──
    readonly navItems: NavItem[] = [
    { id: 'pre-bidding', label: 'Pre-Bidding', icon: 'list' },
    { id: 'bidding', label: 'Bidding', icon: 'home' },
    { id: 'field-survey', label: 'Field Survey', icon: 'star' },
    { id: 'planning', label: 'Planning', icon: 'calendar', hasArrow: true },
    { id: 'execution', label: 'Execution', icon: 'activity' },
    { id: 'qa-qc', label: 'QA / QC', icon: 'arrow-right' },
    { id: 'billing', label: 'Billing', icon: 'align-left' },
    { id: 'row', label: 'ROW Acquistion', icon: 'shopping-bag' },
    ];
    activeNav = signal<string>('planning');
    
    // ── Map toolbar ──
    readonly mapModes = ['2D', '3D'] as const;
    activeMapMode = signal<'2D' | '3D'>('2D');
    
    readonly rightTabs = ['Progress', 'Tasks', 'BOQ', 'Reports'] as const;
    activeRightTab = signal<string>('Progress');
    
    // ── Layers ──
    layers = signal<LayerItem[]>([
    { id: 'roads', label: 'Roads', visible: true },
    { id: 'alignment', label: 'New Aligment', visible: true },
    { id: 'survey', label: 'Survey Points',visible: true },
    { id: 'structures', label: 'Structures', visible: true },
    { id: 'parcels', label: 'Land Parcels', visible: true },
    { id: 'machines', label: 'Machines', visible: true },
    ]);
    
    // ── Project data ──
    readonly project: ProjectSummary = {
    highway: 'NH 48',
    package: '3 +5',
    totalLength: '121.5 km',
    startChainage:'120+000',
    endChainage: '141±500',
    state: 'Maharashtra',
    contractor: 'Larsen & Toubro',
    budget: '₹ 780 cr',
    consultants: 'ABC Consultants',
    };
    
    readonly projectProgress = signal<number>(45);
    
    readonly progressItems = signal<ProgressItem[]>([
    { label: 'Earthwork', pct: 25, color: '#2563eb' },
    { label: 'Culverts', pct: 15, color: '#f59e0b' },
    { label: 'Bridges', pct: 10, color: '#6366f1' },
    { label: 'Pavement', pct: 5, color: '#ec4899' },
    ]);
    
    // ── Contractors ──
    readonly contractors: Contractor[] = [
    { name: 'Larsen & Toubro', initial: 'L', color: '#2563eb' },
    { name: 'ABC Consultancy', initial: 'A', color: '#16a34a' },
    ];
    
    // ── Weather ──
    weather = signal({ temp: 32, rain: 0, icon: '🌤' });
    
    // ── Alerts ──
    alerts = signal<Alert[]>([
    { message: 'Excavator EX102 Idling 2 hours', severity: 'warning' },
    { message: 'Soil Compaction Failure', severity: 'danger' },
    ]);
    
    // ── Chainage labels on map ──
    readonly chainageLabels: ChainageLabel[] = [
    { label: 'CH 120+500', top: 35, left: 31 },
    { label: 'CH 121+500', top: 44, left: 54 },
    { label: 'CH 121+500', top: 55, left: 40 },
    { label: 'CH 121+500', top: 65, left: 33 },
    ];
    
    // ── Search ──
    searchQuery = signal<string>('');
    
    // ── Computed helpers ──
    readonly progressBarWidth = computed(() => `${this.projectProgress()}%`);
    
    alertDotColor(severity: Alert['severity']): string {
    const map = { warning: '#d97706', danger: '#dc2626', info: '#2563eb' };
    return map[severity];
    }
    
    toggleLayer(id: string): void {
    this.layers.update(layers =>
    layers.map(l => l.id === id ? { ...l, visible: !l.visible } : l)
    );
    }
    
    setNav(id: string): void { this.activeNav.set(id); }
    setMapMode(mode: '2D' | '3D'): void { this.activeMapMode.set(mode); }
    setRightTab(tab: string): void { this.activeRightTab.set(tab); }
    
    ngOnInit(): void {
    // Simulate live progress update
    // In production, connect to a WebSocket / REST endpoint
    }
    }